import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Heart } from 'lucide-react';
import { useStore } from '@/store';
import { formatPrice, truncateText, getCache } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { Product } from '@/types';

export function WishlistPage() {
  const { wishlist, toggleWishlist, setWishlist } = useStore();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let allProducts = getCache('products');
        
        if (!allProducts) {
          const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php`);
          if (!response.ok) throw new Error('Failed to fetch products');
          
          const data = await response.json();
          if (!data.status) throw new Error(data.message);
          
          allProducts = data.products;
        }
        
        // Filter out products that are not in stock or not active
        const availableProducts = allProducts.filter((p: Product) => {
          if (p.status !== 1) return false;
          
          if (p.variations && p.variations.length > 0 && p.variations[0].variation !== 'Nil') {
            return p.variations.some(v => parseInt(v.v_qty) > 0);
          }
          return parseInt(p.totalStock.toString()) > 0;
        });

        const wishlistProducts = availableProducts.filter((p: Product) => wishlist.includes(p.id));
        setProducts(wishlistProducts);

        // Update wishlist to remove unavailable products
        const availableIds = wishlistProducts.map(p => p.id);
        if (availableIds.length !== wishlist.length) {
          setWishlist(availableIds);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [wishlist, setWishlist]);

  const handleRemoveFromWishlist = (productId: number) => {
    toggleWishlist(productId);
    toast.success('Removed from wishlist');
  };

  const isProductOutOfStock = (product: Product) => {
    if (product.status !== 1) return true;
    if (product.variations && product.variations.length > 0 && product.variations[0].variation !== 'Nil') {
      return product.variations.every(v => parseInt(v.v_qty) === 0);
    }
    return parseInt(product.totalStock.toString()) === 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-lg mb-4" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Wishlist - Store</title>
        <meta name="description" content="View your wishlist items" />
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">My Wishlist</h1>

          {products.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
              <Heart className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-4">Your wishlist is empty</p>
              <Link to="/products">
                <Button>
                  Continue Shopping
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => {
                const outOfStock = isProductOutOfStock(product);
                return (
                  <div
                    key={product.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden group relative"
                  >
                    {outOfStock && (
                      <div 
                        className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 cursor-pointer"
                        onClick={() => handleRemoveFromWishlist(product.id)}
                      >
                        <div className="text-center p-4">
                          <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-500 text-white font-bold mb-4">
                            SOLD OUT
                          </div>
                          <p className="text-white text-sm">Click to remove from wishlist</p>
                        </div>
                      </div>
                    )}
                    
                    {!outOfStock ? (
                      <Link to={`/product/${product.id}`}>
                        <img
                          src={product.images[0]}
                          alt={product.produt_name}
                          className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                    ) : (
                      <img
                        src={product.images[0]}
                        alt={product.produt_name}
                        className="w-full aspect-square object-cover opacity-75"
                      />
                    )}
                    
                    <div className="p-4">
                      {!outOfStock ? (
                        <Link to={`/product/${product.id}`}>
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-2 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                            {truncateText(product.produt_name, 50)}
                          </h3>
                        </Link>
                      ) : (
                        <h3 className="font-semibold text-gray-500 dark:text-gray-400 mb-2">
                          {truncateText(product.produt_name, 50)}
                        </h3>
                      )}
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {product.category || 'Uncategorized'}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className={`text-lg font-bold ${
                          outOfStock
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {formatPrice(product.sellingprice)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRemoveFromWishlist(product.id);
                          }}
                          className="p-2 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <Heart className="h-5 w-5 fill-current" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}